/**
 * calculator-relations-data.ts — Central registry for project relations.
 *
 * It maps which calculators are related and explains the technical reason
 * behind the relationship based on construction guidelines and building codes.
 *
 * Last verified: July 1, 2026
 */

export interface RelatedCalculatorRelation {
  relatedId: string; // Target calculator ID (e.g. 'paint')
  reason: string; // Explains the connection in trade terms
  sourceName: string; // Governing building standard or authority
  sourceUrl: string; // Auditing URL link
  retrievedDate: string; // YYYY-MM-DD
  notes?: string;
}

export const CALCULATOR_RELATIONS: Record<string, RelatedCalculatorRelation[]> = {
  concrete: [
    {
      relatedId: "mulch",
      reason: "Landscaping transitions and subgrade grading are typically completed concurrently. Proper drainage slopes for both concrete slabs and surrounding beds are critical to prevent erosion.",
      sourceName: "National Ready Mixed Concrete Association (NRMCA)",
      sourceUrl: "https://www.nrmca.org",
      retrievedDate: "2026-07-01",
      notes: "Soil must slope away from concrete edges at a minimum 2% drop."
    },
    {
      relatedId: "tile",
      reason: "Tiling is a standard decorative overlay for concrete slabs. Concrete must cure for a full 28 days to prevent shrinkage cracking from stressing and fracturing the overlying tile grout.",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01",
      notes: "TCNA F102/F185 detailing tiling directly over concrete slabs."
    },
    {
      relatedId: "area",
      reason: "Accurately estimating concrete yards relies entirely on measuring irregular surface shapes. Triangulation helps calculate volume for circular columns or offset footings.",
      sourceName: "Portland Cement Association (PCA)",
      sourceUrl: "https://www.cement.org/cement-concrete-applications/paving/slabs-on-grade",
      retrievedDate: "2026-07-01"
    }
  ],
  paint: [
    {
      relatedId: "drywall",
      reason: "Painting is the immediate follow-up to hanging and finishing drywall. Joint compound mudding must dry completely and be primed before applying any latex base coat.",
      sourceName: "Gypsum Association GA-216",
      sourceUrl: "https://gypsum.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "flooring",
      reason: "Room renovations coordinate paint and flooring schedules. Professional painters recommend coating ceilings and walls before installing floor planks to protect wood from drips.",
      sourceName: "National Wood Flooring Association (NWFA)",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "area",
      reason: "Wall painting calculations require subtracting windows and doors from the total area. Exact measurements prevent ordering excess paint or primer.",
      sourceName: "Sherwin-Williams Product Catalog & Estimator Tools",
      sourceUrl: "https://www.sherwin-williams.com",
      retrievedDate: "2026-07-01"
    }
  ],
  flooring: [
    {
      relatedId: "tile",
      reason: "Tile is a common wet-room alternative to hardwood or luxury vinyl planks. Both flooring types require verifying subfloor deflection levels and flat tolerances to avoid cracked joints.",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "paint",
      reason: "Installing floors is usually coordinated with wall painting. Ceilings and walls should be painted first to avoid tracking paint onto a newly installed floor.",
      sourceName: "National Wood Flooring Association (NWFA)",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "area",
      reason: "Flooring orders require finding net floor square footage. Factoring in a 5% to 20% waste buffer based on layout complexity prevents running short on planks.",
      sourceName: "Flooring America Estimating Standards",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    }
  ],
  area: [
    {
      relatedId: "paint",
      reason: "Paint gallon requirements map directly to wall surface area. Subtracting opening sizes from gross square footage determines the exact net paintable area.",
      sourceName: "Sherwin-Williams Estimating Tools",
      sourceUrl: "https://www.sherwin-williams.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "flooring",
      reason: "Flooring materials are ordered based on floor area calculations. Applying pattern-specific waste percentages ensures you purchase enough boxes for complete rows.",
      sourceName: "National Wood Flooring Association (NWFA)",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "concrete",
      reason: "Concrete slab volume is derived from surface area multiplied by thickness. Knowing exact square footage prevents short-orders during scheduling.",
      sourceName: "Portland Cement Association (PCA)",
      sourceUrl: "https://www.cement.org/cement-concrete-applications/paving/slabs-on-grade",
      retrievedDate: "2026-07-01"
    }
  ],
  fence: [
    {
      relatedId: "concrete",
      reason: "Fence post stability requires post anchors set in concrete. Standard fence line and gate posts must be set in concrete below the frost line to resist wind loads.",
      sourceName: "American Fence Association (AFA)",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "mulch",
      reason: "Landscaping mulch beds frequently border residential fences. Proper slope grading and leaving a dry gap between mulch and wood posts prevents decay.",
      sourceName: "USDA Natural Resources Conservation Service (NRCS)",
      sourceUrl: "https://www.nrcs.usda.gov",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "area",
      reason: "Fence materials are calculated by determining property line boundaries. Confirming perimeter line lengths prevents installation overlap or easement issues.",
      sourceName: "American Fence Association (AFA)",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-01"
    }
  ],
  mulch: [
    {
      relatedId: "concrete",
      reason: "Slabs and walkways are bordered by organic mulch or aggregate beds. Grading bed soil prevents mud washouts onto clean walkways during storms.",
      sourceName: "National Ready Mixed Concrete Association (NRMCA)",
      sourceUrl: "https://www.nrmca.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "fence",
      reason: "Landscaping beds commonly sit along fence lines. Slope gradients must drain water away from wood fence posts to extend their service lifetime.",
      sourceName: "American Fence Association (AFA)",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "area",
      reason: "Mulch volumes rely on measuring bed area in square feet. Applying correct depth factors ensures optimal weed block without root suffocation.",
      sourceName: "USDA Natural Resources Conservation Service (NRCS)",
      sourceUrl: "https://www.nrcs.usda.gov",
      retrievedDate: "2026-07-01"
    }
  ],
  drywall: [
    {
      relatedId: "paint",
      reason: "Hanging drywall must be followed by priming and painting. Proper paint sheens seal and protect the outer face paper of new gypsum boards.",
      sourceName: "Gypsum Association GA-216",
      sourceUrl: "https://gypsum.org",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "tile",
      reason: "Kitchen backsplashes and bathroom walls require gypsum board or cement backerboard under tile adhesive. Proper backing structures support tile weight.",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "area",
      reason: "Drywall orders map to total wall and ceiling square footage. Standard sheet sizes (4x8, 4x12) must be optimized to minimize joint count.",
      sourceName: "Gypsum Association GA-216",
      sourceUrl: "https://gypsum.org",
      retrievedDate: "2026-07-01"
    }
  ],
  tile: [
    {
      relatedId: "flooring",
      reason: "Tile acts as a premium water-impervious flooring option. Both wood floor boards and tiles require verifying subfloor deflection (L/360) and flatness.",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "drywall",
      reason: "Bathroom or kitchen wall tiling requires backing structures. Installing cement board or water-resistant drywall supports tile layout and resists mold.",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      relatedId: "concrete",
      reason: "Tile is often laid over concrete subfloors. Concrete must be cured (28 days) and leveled to avoid cracked tile grout joints.",
      sourceName: "TCNA Handbook F102/F185 Standards",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    }
  ],

  // ── GROUP B — COST ESTIMATOR RELATIONS ────────────────────────────────────

  solar: [
    {
      relatedId: "roofing",
      reason: "Solar panels have a 25-year lifespan. If your roof has less than 10 years of life remaining, a full roof replacement should be completed before any solar installation to avoid costly panel removal and reinstallation later.",
      sourceName: "EnergySage — Roof Readiness Before Solar",
      sourceUrl: "https://news.energysage.com/is-your-roof-ready-for-solar-panels",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "Solar installations represent a major home investment. The ROI calculator helps you compare solar's payback period against other high-value home improvements to prioritize spending.",
      sourceName: "Zillow Research — Solar Home Value Study",
      sourceUrl: "https://www.zillow.com/research/solar-panels-home-value-23551",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "hvac",
      reason: "HVAC systems are the largest electricity consumer in most homes (40–50% of bills). Upgrading to a high-efficiency heat pump in conjunction with solar panels dramatically reduces both energy costs and payback periods.",
      sourceName: "U.S. Department of Energy — Energy Use at Home",
      sourceUrl: "https://www.energy.gov/energysaver/energy-efficient-home-design",
      retrievedDate: "2026-07-02"
    }
  ],

  hvac: [
    {
      relatedId: "solar",
      reason: "HVAC systems are the primary electricity consumer in most US homes. Pairing a high-efficiency heat pump with solar generation can eliminate virtually all heating and cooling electricity costs.",
      sourceName: "U.S. Department of Energy — Home Energy Usage",
      sourceUrl: "https://www.energy.gov/energysaver/energy-efficient-home-design",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "HVAC replacement recovers approximately 45% of cost at resale per NAR 2025 data. Use the ROI calculator to compare HVAC investment against other remodeling projects before committing.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "windows",
      reason: "High-efficiency windows reduce the heating and cooling load on your HVAC system by 10–25%, allowing you to install a smaller (cheaper) system and achieve faster payback.",
      sourceName: "U.S. Department of Energy — Windows & Doors Energy Savings",
      sourceUrl: "https://www.energy.gov/energysaver/update-or-replace-windows",
      retrievedDate: "2026-07-02"
    }
  ],

  roofing: [
    {
      relatedId: "solar",
      reason: "A new roof and solar installation should be planned together. Installing solar on a roof nearing end-of-life requires costly removal and reinstallation of panels. Coordinate both projects for maximum efficiency.",
      sourceName: "SEIA — Solar Installation Best Practices",
      sourceUrl: "https://www.seia.org/initiatives/solar-installation-best-practices",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "Roof replacement recovers ~60% of cost at resale with a Joy Score of 10/10 per NAR 2025. Use the ROI calculator to compare against other high-value projects when prioritizing capital spending.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "hvac",
      reason: "Poor attic ventilation caused by an aging roof accelerates shingle deterioration and dramatically increases HVAC workload. A well-ventilated new roof can reduce attic temperatures by 10–15°F, reducing cooling costs.",
      sourceName: "National Roofing Contractors Association (NRCA) — Ventilation Guide",
      sourceUrl: "https://www.nrca.net",
      retrievedDate: "2026-07-02"
    }
  ],

  kitchen: [
    {
      relatedId: "bathroom",
      reason: "Kitchen and bathroom remodels are often planned together since both involve plumbing, electrical work, and tile — enabling contractors to bundle work for cost savings and minimal disruption.",
      sourceName: "National Kitchen & Bath Association (NKBA) — Project Planning Guide",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "Kitchen renovations recover approximately 60% of cost at resale. The ROI calculator helps you benchmark kitchen investment against other home improvements to optimize your total renovation budget.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "flooring",
      reason: "Kitchen remodels frequently include flooring replacement. Hardwood, LVP, and tile are the most common kitchen floor materials. Flooring should be selected before finalizing cabinet toe-kick and island dimensions.",
      sourceName: "National Wood Flooring Association (NWFA) — Kitchen Flooring Selection",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-02"
    }
  ],

  bathroom: [
    {
      relatedId: "kitchen",
      reason: "Bathroom and kitchen remodels share plumbing, tile, and electrical sub-trades. Bundling both projects with the same contractor can reduce overhead costs by 10–20% and minimize household disruption.",
      sourceName: "National Kitchen & Bath Association (NKBA) — Bundled Project Planning",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "Bathroom renovations recover ~50% of cost at resale with a Joy Score of 9.8/10 — the highest satisfaction rating for any interior remodeling project in the 2025 NAR survey.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "tile",
      reason: "Tile is the dominant material for bathroom floors, showers, and backsplashes. Proper tile selection (porcelain ≥0.42 DCOF for wet floors) and waterproof membrane installation are critical for bathroom longevity.",
      sourceName: "Tile Council of North America (TCNA) / ANSI A326.3",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-02"
    }
  ],

  windows: [
    {
      relatedId: "hvac",
      reason: "New ENERGY STAR windows reduce solar heat gain and heat loss, directly lowering the heating and cooling load on your HVAC system. Window upgrades can reduce HVAC energy use by 10–25% depending on climate.",
      sourceName: "U.S. Department of Energy — Windows Energy Savings",
      sourceUrl: "https://www.energy.gov/energysaver/update-or-replace-windows",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "Vinyl window replacement recovers 74% of cost at resale per NAR 2025. Use the ROI calculator to weigh window replacement against other high-ROI projects like a new front door (100% recovery).",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roofing",
      reason: "Window and roofing projects are often coordinated during major exterior renovations. Both require careful flashing and water management; scheduling them together ensures proper integration of all weather barriers.",
      sourceName: "ASTM International — ASTM E2112 / E1886 Exterior Envelope Standards",
      sourceUrl: "https://www.astm.org",
      retrievedDate: "2026-07-02"
    }
  ],

  deck: [
    {
      relatedId: "concrete",
      reason: "Concrete footings are the structural foundation of every deck. Footings must be sized and placed according to local frost depth and soil bearing capacity to prevent differential settlement.",
      sourceName: "International Residential Code (IRC) — Section R507 / R403",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roi",
      reason: "New decks recover ~50% of their cost at resale per NAR 2025. Use the ROI calculator to compare the deck investment against other exterior improvements like a new front door (100% recovery) or windows (74%).",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "fence",
      reason: "Deck and fence projects are frequently planned together as coordinated outdoor living spaces. Fence posts and deck footings must be located to avoid utility lines and maintain required setbacks from property lines.",
      sourceName: "American Fence Association (AFA) / North American Deck & Railing Association (NADRA)",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-02"
    }
  ],

  "water-heater": [
    {
      relatedId: "hvac",
      reason: "Heat pump water heaters and heat pump HVAC systems share electrical load planning, condensate drainage, and efficiency incentive checks.",
      sourceName: "U.S. Department of Energy — Energy Saver",
      sourceUrl: "https://www.energy.gov/energysaver/water-heating",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "bathroom",
      reason: "Bathroom remodels often expose old shutoffs, undersized hot-water delivery, and recirculation issues that affect water heater sizing.",
      sourceName: "U.S. Department of Energy — Sizing a New Water Heater",
      sourceUrl: "https://www.energy.gov/energysaver/sizing-new-water-heater",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "solar",
      reason: "Electrifying water heating can shift more household energy use to electricity, changing the economics of future solar sizing.",
      sourceName: "U.S. Department of Energy — Water Heating",
      sourceUrl: "https://www.energy.gov/energysaver/water-heating",
      retrievedDate: "2026-07-07"
    }
  ],

  "roof-shingles": [
    {
      relatedId: "roofing",
      reason: "Bundle counts are the material-planning layer; the roofing cost calculator adds labor, tear-off, pitch, and installed-cost assumptions.",
      sourceName: "International Residential Code — Roof Assemblies",
      sourceUrl: "https://codes.iccsafe.org/content/IRC2024P1/chapter-9-roof-assemblies",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "solar",
      reason: "Roof age and roof covering condition should be evaluated before solar installation to avoid removing panels for an early reroof.",
      sourceName: "Solar Energy Industries Association — Installation Best Practices",
      sourceUrl: "https://www.seia.org/initiatives/solar-installation-best-practices",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "windows",
      reason: "Roof edge flashing and upper-wall window flashing both rely on shingle-style water management during exterior envelope work.",
      sourceName: "National Roofing Contractors Association",
      sourceUrl: "https://www.nrca.net",
      retrievedDate: "2026-07-07"
    }
  ],

  siding: [
    {
      relatedId: "windows",
      reason: "Window replacement and siding replacement both depend on proper flashing integration with the wall drainage plane.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "paint",
      reason: "Painted siding and trim projects require measuring the same exterior wall area and openings, even when replacement is not planned.",
      sourceName: "U.S. Department of Energy — Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "Siding is a major exterior investment, so ROI screening helps prevent over-improving homes in lower-price neighborhoods.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  driveway: [
    {
      relatedId: "concrete",
      reason: "Concrete driveways are slab projects; volume, thickness, base, and curing assumptions should be checked before ordering ready-mix.",
      sourceName: "Portland Cement Association",
      sourceUrl: "https://www.cement.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "area",
      reason: "Driveway cost begins with accurate square footage. Irregular flares and turnaround pads should be broken into simple shapes.",
      sourceName: "Federal Highway Administration — Gravel Roads Construction & Maintenance Guide",
      sourceUrl: "https://www.fhwa.dot.gov/construction/pubs/ots15002.pdf",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "mulch",
      reason: "Driveway edges, swales, and adjacent landscape beds need drainage planning to keep runoff from undermining the pavement base.",
      sourceName: "Federal Highway Administration — Gravel Roads Construction & Maintenance Guide",
      sourceUrl: "https://www.fhwa.dot.gov/construction/pubs/ots15002.pdf",
      retrievedDate: "2026-07-07"
    }
  ],

  "home-addition": [
    {
      relatedId: "kitchen",
      reason: "Kitchen additions are among the most expensive room additions because cabinets, plumbing, electrical, ventilation, and appliance circuits stack costs.",
      sourceName: "National Kitchen & Bath Association",
      sourceUrl: "https://nkba.org/research",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "bathroom",
      reason: "Bathroom additions require plumbing, ventilation, GFCI protection, waterproofing, and fixture clearances that materially change cost per square foot.",
      sourceName: "International Residential Code",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "Large additions can exceed neighborhood resale ceilings. ROI screening helps compare new square footage against lower-risk remodels.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  pool: [
    {
      relatedId: "deck",
      reason: "Pool projects often include surrounding deck or patio work for access, safety, furniture, and equipment clearances.",
      sourceName: "U.S. Consumer Product Safety Commission — Pool Safely",
      sourceUrl: "https://www.poolsafely.gov",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "concrete",
      reason: "Pool pads, equipment slabs, and surrounding hardscape frequently require concrete volume estimates before scheduling trades.",
      sourceName: "Portland Cement Association",
      sourceUrl: "https://www.cement.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "Pool resale impact is highly climate- and market-dependent, so project cost should be compared against likely value recovery.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  countertop: [
    {
      relatedId: "kitchen",
      reason: "Countertop replacement is often one line item inside a kitchen remodel budget and should be compared against cabinet and appliance scope.",
      sourceName: "National Kitchen & Bath Association",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "tile",
      reason: "Backsplash tile is commonly installed after countertop replacement so tile layout terminates cleanly at the new counter surface.",
      sourceName: "Tile Council of North America",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "A countertop-only upgrade can improve kitchen presentation without the budget of a full gut remodel, so ROI screening helps size the spend.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  gutters: [
    {
      relatedId: "roofing",
      reason: "Gutters attach at the roof edge and should be coordinated with drip edge, fascia, and roof replacement timing.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "basement-finishing",
      reason: "Controlling roof runoff reduces foundation moisture risk before investing in finished basement walls and flooring.",
      sourceName: "U.S. Environmental Protection Agency — Soak Up the Rain",
      sourceUrl: "https://www.epa.gov/soakuptherain",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "siding",
      reason: "Failed gutters can wet fascia and siding; replacement is often coordinated with exterior cladding and trim repairs.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov",
      retrievedDate: "2026-07-07"
    }
  ],

  "basement-finishing": [
    {
      relatedId: "drywall",
      reason: "Finished basements require drywall quantities, moisture-tolerant assemblies, and fireblocking details before close-in.",
      sourceName: "International Residential Code",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "flooring",
      reason: "Basement flooring choices depend on slab moisture risk, ceiling height, and comfort requirements.",
      sourceName: "U.S. Environmental Protection Agency — Mold Resources",
      sourceUrl: "https://www.epa.gov/mold",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "Basement conversion can add usable square footage but resale value depends on legal egress, ceiling height, and local comps.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  insulation: [
    {
      relatedId: "hvac",
      reason: "Insulation and air sealing reduce heating and cooling loads, which affects HVAC sizing and operating cost.",
      sourceName: "U.S. Department of Energy — Insulation",
      sourceUrl: "https://www.energy.gov/energysaver/insulation",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "windows",
      reason: "Envelope upgrades should compare attic insulation, air leakage, and window performance before spending on one measure alone.",
      sourceName: "ENERGY STAR — Seal and Insulate",
      sourceUrl: "https://www.energystar.gov/saveathome/seal_insulate",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roofing",
      reason: "Attic insulation should preserve ventilation pathways so roof sheathing and shingles are not exposed to excess heat or moisture.",
      sourceName: "U.S. Department of Energy — Insulation",
      sourceUrl: "https://www.energy.gov/energysaver/insulation",
      retrievedDate: "2026-07-07"
    }
  ],

  roi: [
    {
      relatedId: "kitchen",
      reason: "Kitchen renovations are one of the most common high-ROI projects (60% cost recovery). Use the Kitchen Remodel calculator to generate an accurate project cost, then input it here to benchmark against other improvements.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "bathroom",
      reason: "Bathroom renovations score 9.8/10 on the NAR Joy Score and recover 50% at resale. Use the Bathroom Remodel calculator to estimate your specific project cost for an accurate ROI projection.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      relatedId: "roofing",
      reason: "Roof replacement has one of the strongest combination of financial ROI (60%) and Joy Score (10/10) in the NAR report. Use the Roofing calculator to get an accurate replacement estimate for your home.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    }
  ],

  "cabinet-refacing": [
    {
      relatedId: "kitchen",
      reason: "Cabinets usually consume a large share of kitchen remodel budgets, so cabinet scope should be checked against the full kitchen budget.",
      sourceName: "National Kitchen & Bath Association",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "countertop",
      reason: "Cabinet replacement can affect countertop templating, support, and removal risk.",
      sourceName: "National Kitchen & Bath Association",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "Cabinet-heavy kitchen updates should be screened against expected resale recovery and neighborhood price ceilings.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  "backup-generator": [
    {
      relatedId: "hvac",
      reason: "Central AC and furnace blowers are major generator loads and can materially change required kilowatts.",
      sourceName: "Ready.gov — Power Outages",
      sourceUrl: "https://www.ready.gov/power-outages",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "solar",
      reason: "Battery-backed solar and standby generators solve different outage needs; comparing both helps right-size resilience spending.",
      sourceName: "U.S. Department of Energy",
      sourceUrl: "https://www.energy.gov",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "ev-charger",
      reason: "Large electrical loads such as EV chargers and standby generators both require panel capacity review and permitted electrical work.",
      sourceName: "National Fire Protection Association — National Electrical Code",
      sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
      retrievedDate: "2026-07-07"
    }
  ],

  "ev-charger": [
    {
      relatedId: "solar",
      reason: "Home EV charging increases household electricity use, so solar sizing and EV charging plans should be coordinated.",
      sourceName: "U.S. Department of Energy AFDC",
      sourceUrl: "https://afdc.energy.gov/fuels/electricity-stations",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "backup-generator",
      reason: "EV chargers and generators both affect panel load calculations and may need electrical service upgrades.",
      sourceName: "National Fire Protection Association — National Electrical Code",
      sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "roi",
      reason: "EV charger installation is a lifestyle and utility upgrade, so cost should be benchmarked against other home investments.",
      sourceName: "Internal Revenue Service — About Form 8911",
      sourceUrl: "https://www.irs.gov/forms-pubs/about-form-8911",
      retrievedDate: "2026-07-07"
    }
  ],

  "siding-material": [
    {
      relatedId: "siding",
      reason: "Material takeoff feeds directly into installed siding replacement cost and waste allowances.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "area",
      reason: "Siding material quantity begins with accurate wall, gable, and opening square footage.",
      sourceName: "Polymeric Exterior Products Association",
      sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "windows",
      reason: "Siding and window work share flashing, trim, and drainage-plane interfaces.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov",
      retrievedDate: "2026-07-07"
    }
  ],

  "retaining-wall": [
    {
      relatedId: "concrete",
      reason: "Retaining walls often need concrete footings, caps, drain outlets, or adjacent pads.",
      sourceName: "National Concrete Masonry Association",
      sourceUrl: "https://ncma.org",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "mulch",
      reason: "Retaining walls are commonly paired with landscape beds where surface drainage and soil cover must be coordinated.",
      sourceName: "USDA Natural Resources Conservation Service",
      sourceUrl: "https://www.nrcs.usda.gov",
      retrievedDate: "2026-07-07"
    },
    {
      relatedId: "pool",
      reason: "Sloped pool sites frequently require retaining walls to hold grade around decking and equipment areas.",
      sourceName: "U.S. Consumer Product Safety Commission — Pool Safely",
      sourceUrl: "https://www.poolsafely.gov",
      retrievedDate: "2026-07-07"
    }
  ]
};
